import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { FlexView, Row, Separator, Text } from '@cuemath/comps';
import { Button } from '@cuemath/web-comps';
import BallLoader from 'comps/ball-loader';
import COLORS from 'constants/colors';
import { closeModal } from 'actions/modal';
import { DEFAULT_CURRENCY } from 'screens/region-payout/constants';

const Wrapper = styled(FlexView)`
  padding: 32px 24px 24px;
`;

const Detail = styled(FlexView)`
  background-color: ${COLORS.WILD_SAND};
  border-radius: 8px;
  padding: 16px;
`;

const DetailRow = styled(Row)`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const EntryList = styled(FlexView)`
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid ${COLORS.GALLERY};
  border-radius: 8px;
`;

const EntryRow = styled(Row)`
  padding: 8px 16px;
  border-bottom: 1px solid ${COLORS.GALLERY};
  &:last-child {
    border-bottom: none;
  }
`;

const Warning = styled(FlexView)`
  background-color: ${COLORS.WARNING_BG};
  border-radius: 8px;
  padding: 12px 16px;
`;

const ButtonRow = styled(Row)`
  gap: 12px;
`;

const RegionPayoutBulkConfirm = ({
  count,
  region,
  ratio,
  currency = DEFAULT_CURRENCY,
  validityStart,
  validityEnd,
  entries = [],
  onConfirm,
  actions,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = () => {
    setLoading(true);
    onConfirm();
  };

  return (
    <Wrapper>
      <Text variant="h2" align="center">
        Create {count} payout window{count > 1 ? 's' : ''}
      </Text>
      <Separator height={8} transparent />
      <Text variant="body2" align="center" color={COLORS.GRAY}>
        {region} · {ratio}
      </Text>
      <Separator height={20} transparent />

      <Detail>
        <DetailRow justifyContent="space-between" alignItems="center">
          <Text variant="body2" color={COLORS.GRAY}>
            Valid from
          </Text>
          <Text variant="body1Medium">{validityStart}</Text>
        </DetailRow>
        <DetailRow justifyContent="space-between" alignItems="center">
          <Text variant="body2" color={COLORS.GRAY}>
            Valid until
          </Text>
          <Text variant="body1Medium">{validityEnd || 'open'}</Text>
        </DetailRow>
      </Detail>

      <Separator height={16} transparent />

      <EntryList>
        {entries.map(e => (
          <EntryRow
            key={`${e.grade}|${e.tier}`}
            justifyContent="space-between"
            alignItems="center"
          >
            <Text variant="body2" color={COLORS.GRAY}>
              {e.grade} · {e.tier}
            </Text>
            <Text variant="body1Medium" color={COLORS.PRIMARY}>
              {currency} {e.fee}
            </Text>
          </EntryRow>
        ))}
      </EntryList>

      <Separator height={16} transparent />

      <Warning>
        <Text variant="body2" color={COLORS.ABBEY}>
          Each row creates a new effective-dated window. Existing fees keep their
          snapshot; this affects future fees only. A window that overlaps an existing one
          is rejected and flagged on that cell.
        </Text>
      </Warning>

      <Separator height={24} transparent />

      <ButtonRow justifyContent="center">
        {loading ? (
          <BallLoader />
        ) : (
          <>
            <Button
              variant="secondary"
              size="small"
              label="Cancel"
              width="160px"
              onClick={() => actions.closeModal()}
            />
            <Button
              size="small"
              label={`Create ${count}`}
              width="160px"
              onClick={handleConfirm}
            />
          </>
        )}
      </ButtonRow>
    </Wrapper>
  );
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ closeModal }, dispatch),
});

export default connect(null, mapDispatchToProps)(RegionPayoutBulkConfirm);
